/**
 * L'écran « Distance » : une table de distances par site, entre le site et les communes.
 *
 * *Demande de Chris, 02/09/2026, classeur `Michelin.xlsx` à l'appui : « il faut créer un menu
 * Distance dans lequel tu dois intégrer les tables de distances, une table par site. Nous devrons
 * pouvoir modifier, supprimer, ou ajouter des villes dans chaque table. Je dois pouvoir aussi
 * intégrer une nouvelle table si un nouveau site est créé. Je dois pouvoir exporter ces tables
 * sous Excel puis les réimporter. […] Tu comprends que toutes ces tables auront un rôle important
 * dans l'organisation du transport plus tard. »*
 *
 * *Et : « dans les ressources génériques il y a une table des sites […] si on veut créer une
 * nouvelle table il faudra se référer à la table référentiel des sites. »*
 *
 * ## Une table à la fois, et une recherche — pas quatre mille lignes
 *
 * C'est la différence de nature avec les autres référentiels du produit : 40 codes de
 * main-d'œuvre, 62 matériels, 6 zones… et ici **4 052 communes par site**, sept fois. Un tableau
 * de quatre mille lignes ne se lit pas, il se cherche : l'écran montre une table à la fois, un
 * champ de recherche, et cent lignes par page.
 *
 * Le **total** est écrit à côté du nombre affiché, toujours. Sans lui, une recherche qui ne rend
 * rien et une table vide se lisent pareil — le pire mode de panne d'un écran de recherche.
 *
 * ## Ce qui appartient à la ligne, et ce qui appartient à la commune
 *
 * La distance et le prix de carburant appartiennent au **couple** (ce site, cette commune) : ils
 * se saisissent ici. Le nom, le code postal, le département et le point appartiennent à la
 * **commune**, partagée par les sept tables — les corriger ici les corrige partout, et l'écran le
 * dit avant qu'on s'en étonne. C'est le but : un code postal faux se corrige une fois.
 */
import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import {
  adresseClasseurDistances,
  ajouterCommuneALaTable,
  alignerLesTablesDeDistances,
  chargerDistancier,
  chargerTableDistance,
  creerTableDistance,
  fixerDistance,
  importerDistances,
  modifierCommuneDistance,
  retirerCommuneDeLaTable,
  supprimerTableDistance,
} from "./api";
import type {
  BilanImportDistances,
  ContenuTableDistance,
  Distancier,
  LigneDistance,
} from "./api";

/** Un champ qui n'envoie sa valeur qu'une fois la saisie terminée. */
function Champ({
  valeur,
  placeholder,
  enAttente,
  ariaLabel,
  onValider,
}: {
  valeur: string;
  placeholder?: string;
  enAttente: boolean;
  ariaLabel: string;
  onValider: (valeur: string) => void;
}): JSX.Element {
  const [brouillon, setBrouillon] = useState<string | null>(null);

  return (
    <input
      className={`saisie${enAttente ? " saisie--attente" : ""}`}
      value={brouillon ?? valeur}
      placeholder={placeholder}
      disabled={enAttente}
      aria-label={ariaLabel}
      onChange={(e) => {
        setBrouillon(e.target.value);
      }}
      onFocus={(e) => {
        e.target.select();
      }}
      onBlur={() => {
        const saisi = brouillon;
        setBrouillon(null);
        if (saisi !== null && saisi.trim() !== valeur) onValider(saisi.trim());
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setBrouillon(null);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/**
 * Le compte rendu d'un import, onglet par onglet.
 *
 * Un onglet par site, chacun avec son sort : rattaché, créé, refusé. Un bilan global aurait caché
 * l'onglet resté de côté, et c'est précisément celui qu'il faut voir.
 */
function BilanImport({ bilan }: { bilan: BilanImportDistances }): JSX.Element {
  const pris = bilan.onglets.filter((o) => o.refus === null);
  const laisses = bilan.onglets.filter((o) => o.refus !== null);

  return (
    <div className="import__bilan">
      <p>
        <strong>{pris.length}</strong> onglet(s) intégré(s)
        {laisses.length > 0 && (
          <>
            , <strong>{laisses.length}</strong> laissé(s) de côté
          </>
        )}
        .
      </p>

      {pris.map((o) => (
        <div key={o.onglet} className="distances__bilan-onglet">
          <p>
            <strong>{o.onglet}</strong> → {o.site}
            {o.rattachePar === "nom" && (
              <span className="note">
                {" "}
                (rattaché par son nom, faute de colonne « CodeSite »)
              </span>
            )}
            {o.tableCreee && <strong> · table créée</strong>}
          </p>
          <p>
            {o.lignesLues} ligne(s) lue(s) · {o.communesAjoutees} commune(s) ajoutée(s) à la table
            {o.communesCreees > 0 && <> · {o.communesCreees} commune(s) créée(s) au référentiel</>}{" "}
            · {o.distancesPosees} distance(s) posée(s)
            {o.distancesEffacees > 0 && (
              <>
                {" "}
                · <strong>{o.distancesEffacees} distance(s) effacée(s)</strong> — la cellule était
                vide dans le classeur
              </>
            )}
            .
          </p>
          {/*
            Ce que l'import a VU et n'a PAS pris : les communes déjà connues dont le classeur dit
            autre chose. Les recopier effacerait un géocodage fait depuis dans l'application ; les
            ignorer en silence serait pire.
          */}
          {o.ecartsDeCommune.length > 0 && (
            <>
              <p className="note">
                {o.ecartsDeCommune.length} écart(s) sur des communes déjà connues — <strong>rien
                n'a été réécrit</strong>, une commune se corrige à l'écran :
              </p>
              <ul className="import__refusees">
                {o.ecartsDeCommune.map((e) => (
                  <li key={`${e.commune}-${e.champ}`}>
                    {e.commune} · {e.champ} : base « {e.base} », classeur « {e.classeur} »
                  </li>
                ))}
              </ul>
            </>
          )}
          {o.refusees.length > 0 && (
            <>
              <p className="alerte">
                {o.refusees.length} ligne(s) refusée(s) — rien n'a été inventé à leur place :
              </p>
              <ul className="import__refusees">
                {o.refusees.map((r) => (
                  <li key={`${String(r.ligne)}-${r.raison}`}>
                    {r.ligne > 0 ? `ligne ${String(r.ligne)} : ` : ""}
                    {r.raison}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      ))}

      {laisses.length > 0 && (
        <>
          <p className="alerte">Onglets laissés de côté — le reste du classeur est passé :</p>
          <ul className="import__refusees">
            {laisses.map((o) => (
              <li key={o.onglet}>
                <strong>{o.onglet}</strong> : {o.refus}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** Le panneau qui choisit la table, en crée une, ou la supprime. */
function ChoixDeTable({
  distancier,
  tableId,
  enAttente,
  onChoisir,
  onAgir,
}: {
  distancier: Distancier;
  tableId: string | null;
  enAttente: boolean;
  onChoisir: (id: string) => void;
  onAgir: (action: Promise<unknown>) => void;
}): JSX.Element {
  const [siteVoulu, setSiteVoulu] = useState("");
  const courante = distancier.tables.find((t) => t.id === tableId) ?? null;

  return (
    <div className="periodes">
      <p className="etat">
        <label>
          Table de distances{" "}
          <select
            value={courante?.id ?? ""}
            disabled={distancier.tables.length === 0}
            aria-label="Table de distances à afficher"
            onChange={(e) => {
              onChoisir(e.target.value);
            }}
          >
            {distancier.tables.length === 0 && <option value="">aucune table</option>}
            {distancier.tables.map((t) => (
              <option key={t.id} value={t.id}>
                {t.site.code} — {t.site.denomination} ({t.communes} commune(s))
              </option>
            ))}
          </select>
        </label>
      </p>

      {/*
        **La liste des sites vient du RÉFÉRENTIEL**, sur la consigne de Chris : « si on veut créer
        une nouvelle table il faudra se référer à la table référentiel des sites ». Un site qui n'y
        est pas ne peut pas avoir de table — il faut d'abord le créer dans « Carrières et sites du
        groupe », et l'écran le dit plutôt que de laisser deviner devant un menu vide.

        C'est la règle du menu d'ajout du paramétrage transport (01/09) : ce qui est vide doit dire
        pourquoi il l'est.
      */}
      <div className="ajout">
        <span className="periodes__titre">Nouvelle table</span>
        <select
          className="ajout__libelle"
          value={siteVoulu}
          disabled={distancier.sitesSansTable.length === 0}
          aria-label="Site pour lequel créer une table de distances"
          onChange={(e) => {
            setSiteVoulu(e.target.value);
          }}
        >
          <option value="">
            {distancier.sitesSansTable.length === 0
              ? "Tous les sites du référentiel ont leur table"
              : "Choisir un site…"}
          </option>
          {distancier.sitesSansTable.map((s) => (
            <option key={s.id} value={s.id}>
              {s.code} — {s.denomination}
              {s.actif ? "" : " (retiré)"}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={siteVoulu === "" || enAttente}
          onClick={() => {
            const choisi = siteVoulu;
            setSiteVoulu("");
            onAgir(creerTableDistance(choisi));
          }}
        >
          Créer la table
        </button>
      </div>

      {/*
        **L'invariant, affiché.** *Chris, 02/09/2026 : « la liste des communes doit être identique
        pour tous les sites ».* Chaque geste le tient — créer une table la remplit de toute la
        liste, ajouter une ville l'ajoute partout, en retirer une la retire partout —, mais un
        invariant qu'on ne montre pas n'en est pas un. S'il tombe, le bouton le rétablit.
      */}
      <p className="etat">
        {distancier.listesAlignees ? (
          <>
            Les <strong>{distancier.tables.length}</strong> tables portent la{" "}
            <strong>même liste de {distancier.communes} communes</strong>. C'est la règle : une
            distance peut manquer — elle est à calculer —, une commune non, sinon deux sites ne
            seraient plus comparables.
          </>
        ) : (
          <>
            <strong className="alerte">
              Les listes de communes ne sont plus identiques :{" "}
              {distancier.manquantes.map((m) => `${m.site} (${String(m.combien)})`).join(", ")}.
            </strong>{" "}
            <button
              type="button"
              className="lien"
              disabled={enAttente}
              onClick={() => {
                onAgir(alignerLesTablesDeDistances());
              }}
            >
              Réaligner les tables
            </button>{" "}
            <span className="note">
              — les communes manquantes sont ajoutées sans distance, donc à calculer. Aucune
              distance existante n'est touchée.
            </span>
          </>
        )}
      </p>

      <p className="etat">
        Le choix vient du référentiel <strong>« Carrières et sites du groupe »</strong> des
        ressources génériques :{" "}
        {distancier.tables.length} de ses{" "}
        {distancier.tables.length + distancier.sitesSansTable.length} sites ont une table.{" "}
        {distancier.sitesSansTable.length === 0 ? (
          <strong>
            Il n'en reste aucun — pour une table de plus, créer d'abord le site dans « Référentiels ».
          </strong>
        ) : (
          <>
            {distancier.sitesSansTable.length} encore disponible(s). Un site qui n'y figure pas doit
            y être créé d'abord.
          </>
        )}{" "}
        Le référentiel des communes en compte <strong>{distancier.communes}</strong>.
      </p>

      {/*
        La suppression d'une table emporte ses distances, et il peut y en avoir quatre mille. Le
        nombre est donc CONFIRMÉ dans la demande, et le serveur refuse s'il ne correspond plus —
        même parti pris que la suppression d'un chantier, qui demande son n° d'affaire. Une
        confirmation qui oblige à lire ce qu'on détruit.
      */}
      {courante !== null && (
        <p className="etat">
          <button
            type="button"
            className="lien"
            disabled={enAttente}
            onClick={() => {
              const combien = courante.communes;
              const message =
                combien === 0
                  ? `Supprimer la table de « ${courante.site.denomination} » ? Elle est vide.`
                  : `Supprimer la table de « ${courante.site.denomination} » et ses ${String(combien)} distance(s) ? Le référentiel des communes n'est pas touché.`;
              if (!window.confirm(message)) return;
              onAgir(supprimerTableDistance(courante.id, combien));
            }}
          >
            Supprimer la table de « {courante.site.denomination} »
          </button>
          <span className="note">
            {" "}
            — elle emporte ses {courante.communes} distance(s). Les communes elles-mêmes restent au
            référentiel, et les autres tables n'y touchent pas.
          </span>
        </p>
      )}
    </div>
  );
}

/** Le formulaire d'ajout d'une commune à la table affichée. */
function AjoutDeCommune({
  tableId,
  enAttente,
  onAgir,
}: {
  tableId: string;
  enAttente: boolean;
  onAgir: (action: Promise<unknown>) => void;
}): JSX.Element {
  const [saisie, setSaisie] = useState({
    nom: "",
    codePostal: "",
    codeDepartement: "",
    distanceKm: "",
    prixCarburant: "",
  });
  const complet = saisie.nom.trim() !== "" && saisie.codePostal.trim() !== "";

  return (
    <>
      <form
        className="ajout"
        onSubmit={(e) => {
          e.preventDefault();
          if (!complet) return;
          const demande = {
            nom: saisie.nom.trim(),
            codePostal: saisie.codePostal.trim(),
            codeDepartement: saisie.codeDepartement.trim() === "" ? null : saisie.codeDepartement.trim(),
            distanceKm: saisie.distanceKm === "" ? null : saisie.distanceKm,
            prixCarburant: saisie.prixCarburant === "" ? null : saisie.prixCarburant,
          };
          setSaisie({ nom: "", codePostal: "", codeDepartement: "", distanceKm: "", prixCarburant: "" });
          onAgir(ajouterCommuneALaTable(tableId, demande));
        }}
      >
        <span className="periodes__titre">Ajouter une commune</span>
        <input
          className="ajout__libelle"
          value={saisie.nom}
          placeholder="Nom de la commune"
          aria-label="Nom de la commune à ajouter"
          onChange={(e) => {
            setSaisie({ ...saisie, nom: e.target.value });
          }}
        />
        <input
          className="ajout__nombre"
          value={saisie.codePostal}
          placeholder="CP"
          aria-label="Code postal de la commune à ajouter"
          onChange={(e) => {
            setSaisie({ ...saisie, codePostal: e.target.value });
          }}
        />
        <input
          className="ajout__nombre"
          value={saisie.codeDepartement}
          placeholder="Dép."
          aria-label="Département de la commune à ajouter"
          onChange={(e) => {
            setSaisie({ ...saisie, codeDepartement: e.target.value });
          }}
        />
        <label>
          distance{" "}
          <input
            className="ajout__nombre"
            value={saisie.distanceKm}
            placeholder="km"
            aria-label="Distance de la commune à ajouter"
            onChange={(e) => {
              setSaisie({ ...saisie, distanceKm: e.target.value });
            }}
          />
        </label>
        <label>
          carburant{" "}
          <input
            className="ajout__nombre"
            value={saisie.prixCarburant}
            placeholder="€"
            aria-label="Prix de carburant de la commune à ajouter"
            onChange={(e) => {
              setSaisie({ ...saisie, prixCarburant: e.target.value });
            }}
          />
        </label>
        <button type="submit" disabled={!complet || enAttente}>
          Ajouter
        </button>
      </form>
      <p className="note">
        La commune est identifiée par son <strong>nom et son code postal</strong> : 76 noms se
        répètent dans ce référentiel, jusqu'à quatre fois — quatre Mazerolles, quatre Monségur,
        quatre Montaut.
        <br />
        {/*
          **Ajouter une ville l'ajoute à TOUTES les tables.** *Chris, 02/09/2026 : « la liste des
          communes doit être identique pour tous les sites ».* Dit ici, parce que la surprise
          serait mauvaise dans les deux sens : croire qu'on n'ajoute qu'ici, ou croire qu'il faut
          recommencer six fois.
        */}
        Elle est ajoutée à <strong>toutes les tables</strong> — la liste doit rester identique
        pour tous les sites. La distance saisie ne vaut que pour la table affichée ; ailleurs elle
        reste <strong>à calculer</strong>, ce que l'écran compte. Une distance laissée vide ici est
        permise pour la même raison.
      </p>
    </>
  );
}

type Etat =
  | { phase: "chargement" }
  | { phase: "charge"; distancier: Distancier }
  | { phase: "erreur"; raison: string };

/** Ce qui manque sur une ligne, pour le signaler à sa place. */
function sansDistance(l: LigneDistance): boolean {
  return l.distanceKm === null;
}

export function Distances(): JSX.Element {
  const [etat, setEtat] = useState<Etat>({ phase: "chargement" });
  const [tableId, setTableId] = useState<string | null>(null);
  const [contenu, setContenu] = useState<ContenuTableDistance | null>(null);
  const [recherche, setRecherche] = useState("");
  const [rechercheAppliquee, setRechercheAppliquee] = useState("");
  const [decalage, setDecalage] = useState(0);
  const [enAttente, setEnAttente] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [bilan, setBilan] = useState<BilanImportDistances | null>(null);
  const champFichier = useRef<HTMLInputElement>(null);

  const rechargerDistancier = (): Promise<Distancier> =>
    chargerDistancier().then((distancier) => {
      setEtat({ phase: "charge", distancier });
      return distancier;
    });

  useEffect(() => {
    rechargerDistancier()
      .then((distancier) => {
        setTableId((actuel) => actuel ?? distancier.tables[0]?.id ?? null);
      })
      .catch((e: unknown) => {
        setEtat({ phase: "erreur", raison: e instanceof Error ? e.message : String(e) });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rechargerTable = (
    id: string | null = tableId,
    options: { recherche?: string; decalage?: number } = {},
  ): void => {
    if (id === null) {
      setContenu(null);
      return;
    }
    chargerTableDistance(id, {
      recherche: options.recherche ?? rechercheAppliquee,
      decalage: options.decalage ?? decalage,
    })
      .then(setContenu)
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      });
  };

  useEffect(() => {
    rechargerTable(tableId, { recherche: rechercheAppliquee, decalage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, rechercheAppliquee, decalage]);

  /** Tout geste passe par ici : une seule façon d'attendre, d'échouer et de recharger. */
  const agir = (action: Promise<unknown>): void => {
    setEnAttente(true);
    setErreur(null);
    action
      .then(async () => {
        const distancier = await rechargerDistancier();
        // La table affichée peut avoir disparu — on vient peut-être de la supprimer.
        const suivante = distancier.tables.find((t) => t.id === tableId)?.id ?? distancier.tables[0]?.id ?? null;
        if (suivante !== tableId) setTableId(suivante);
        else rechargerTable(suivante);
      })
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setEnAttente(false);
      });
  };

  if (etat.phase === "chargement") return <p>Chargement des tables de distances…</p>;
  if (etat.phase === "erreur") return <p className="alerte">{etat.raison}</p>;

  const { distancier } = etat;
  const derniere = contenu === null ? 0 : Math.min(contenu.decalage + contenu.limite, contenu.total);

  return (
    <>
      <p className="etat">
        Une table de distances par site : ce qui sépare le site de chaque commune, en kilomètres.
        Les tables sont <strong>indépendantes les unes des autres</strong>, mais elles partagent le{" "}
        <strong>référentiel des communes</strong> — nom, code postal, département, coordonnées. Une
        commune s'y corrige à un seul endroit, et la correction se voit dans toutes les tables.
      </p>
      <p className="etat">
        <strong>Ce qui appartient à la ligne</strong> : la distance et le prix de carburant, propres
        au couple site/commune. <strong>Ce qui appartient à la commune</strong> : tout le reste,
        partagé. Une distance vide est une distance <strong>à calculer</strong>, jamais un zéro —
        un zéro se lirait « sur place ».
      </p>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      <ChoixDeTable
        distancier={distancier}
        tableId={tableId}
        enAttente={enAttente}
        onChoisir={(id) => {
          setTableId(id);
          setDecalage(0);
        }}
        onAgir={agir}
      />

      {/*
        L'aller-retour par Excel. L'export SANS table choisie sort tout — un onglet par site, la
        forme du fichier d'origine. Le réimport lit chaque onglet séparément et dit ce qu'il a fait
        de chacun : un onglet qu'il ne sait pas rattacher n'empêche pas les autres d'entrer.
      */}
      <p className="etat">
        <a href={adresseClasseurDistances(null)} download>
          Exporter toutes les tables
        </a>
        {tableId !== null && (
          <>
            {" · "}
            <a href={adresseClasseurDistances(tableId)} download>
              Exporter cette table seule
            </a>
          </>
        )}
        {" · "}
        <button type="button" className="lien" onClick={() => champFichier.current?.click()}>
          Réimporter un classeur
        </button>
        <input
          ref={champFichier}
          type="file"
          accept=".xlsx,.xls"
          hidden
          onChange={(e) => {
            const fichier = e.target.files?.[0];
            e.target.value = "";
            if (fichier === undefined) return;
            setBilan(null);
            setErreur(null);
            setEnAttente(true);
            importerDistances(fichier)
              .then((resultat) => {
                setBilan(resultat);
                agir(Promise.resolve());
              })
              .catch((err: unknown) => {
                setErreur(err instanceof Error ? err.message : String(err));
              })
              .finally(() => {
                setEnAttente(false);
              });
          }}
        />
      </p>
      <p className="note">
        Un <strong>onglet par site</strong>, et le site se lit dans la colonne « CodeSite » — à
        défaut dans « NomSite », en comparant les dénominations du référentiel. Jamais dans le nom
        de l'onglet : Excel le plafonne à 31 caractères, et deux onglets du classeur d'origine y
        sont déjà tronqués. Un onglet qui n'est pas une table de distances est laissé de côté et
        signalé, pas rejeté avec le reste.
        <br />
        <strong>Ce que le réimport fait</strong> : il ajoute les communes du classeur, <strong>ne
        retire jamais</strong> celles qu'il ne porte pas, <strong>efface</strong> une distance dont
        la cellule est vide, et <strong>ne réécrit pas</strong> une commune déjà connue — il montre
        alors ce qui diffère.
      </p>

      {bilan !== null && <BilanImport bilan={bilan} />}

      {contenu === null ? (
        <p className="alerte">
          {distancier.tables.length === 0
            ? "Aucune table de distances. En créer une ci-dessus pour un site du référentiel, ou déposer le classeur."
            : "Choisir une table ci-dessus."}
        </p>
      ) : (
        <section className="referentiel">
          <h2>
            {contenu.table.site.code} — {contenu.table.site.denomination} ·{" "}
            {contenu.table.communes} commune(s)
          </h2>

          {/* Ce qui manque se COMPTE et s'affiche. Un tableau qui se tait se lit « tout va bien ». */}
          <p className="etat">
            {contenu.table.sansDistance === 0 ? (
              <>Toutes les communes de cette table ont une distance.</>
            ) : (
              <strong>
                {contenu.table.sansDistance} commune(s) sans distance — à calculer.
              </strong>
            )}
          </p>

          <form
            className="ajout"
            onSubmit={(e) => {
              e.preventDefault();
              setDecalage(0);
              setRechercheAppliquee(recherche.trim());
            }}
          >
            <span className="periodes__titre">Chercher</span>
            <input
              className="ajout__libelle"
              value={recherche}
              placeholder="Nom de commune, code postal, ou département"
              aria-label="Chercher une commune dans cette table"
              onChange={(e) => {
                setRecherche(e.target.value);
              }}
            />
            <button type="submit">Chercher</button>
            {rechercheAppliquee !== "" && (
              <button
                type="button"
                className="lien"
                onClick={() => {
                  setRecherche("");
                  setRechercheAppliquee("");
                  setDecalage(0);
                }}
              >
                tout afficher
              </button>
            )}
          </form>

          {/*
            Le TOTAL à côté du nombre affiché, toujours. Sans lui, une recherche qui ne rend rien
            et une table vide se lisent pareil — et sur quatre mille lignes, on ne peut pas
            vérifier à l'œil.
          */}
          <p className="etat">
            {contenu.total === 0 ? (
              rechercheAppliquee === "" ? (
                <strong>Cette table est vide : aucune commune.</strong>
              ) : (
                <strong>Aucune commune ne répond à « {rechercheAppliquee} » dans cette table.</strong>
              )
            ) : (
              <>
                Lignes {contenu.decalage + 1} à {derniere} sur <strong>{contenu.total}</strong>
                {rechercheAppliquee !== "" && <> qui répondent à « {rechercheAppliquee} »</>}.
              </>
            )}
            {contenu.total > contenu.limite && (
              <>
                {" · "}
                <button
                  type="button"
                  className="lien"
                  disabled={contenu.decalage === 0}
                  onClick={() => {
                    setDecalage(Math.max(contenu.decalage - contenu.limite, 0));
                  }}
                >
                  page précédente
                </button>
                {" · "}
                <button
                  type="button"
                  className="lien"
                  disabled={derniere >= contenu.total}
                  onClick={() => {
                    setDecalage(contenu.decalage + contenu.limite);
                  }}
                >
                  page suivante
                </button>
              </>
            )}
          </p>

          <div className="transport__defilement">
            <table className="ressources transport__table distances__table">
              {/* La grille des colonnes : sans elle, `table-layout: fixed` sur `width: 100%`
                  partage la fenêtre à parts égales — la panne corrigée le 01/09/2026. */}
              <colgroup>
                <col style={{ width: "calc(26ch + 0.7rem)" }} />
                <col style={{ width: "calc(8ch + 0.7rem)" }} />
                <col style={{ width: "calc(6ch + 0.7rem)" }} />
                <col style={{ width: "calc(11ch + 0.7rem)" }} />
                <col style={{ width: "calc(11ch + 0.7rem)" }} />
                <col style={{ width: "calc(10ch + 0.7rem)" }} />
                <col style={{ width: "calc(10ch + 0.7rem)" }} />
                <col style={{ width: "5.5rem" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Commune</th>
                  <th className="col-nombre">CP</th>
                  <th className="col-nombre">Dép.</th>
                  <th className="col-nombre">Latitude</th>
                  <th className="col-nombre">Longitude</th>
                  <th className="col-nombre">Distance (km)</th>
                  <th className="col-nombre">Carburant (€)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {contenu.lignes.map((l) => (
                  <tr key={l.id} className={sansDistance(l) ? "distances__sans" : undefined}>
                    {/*
                      Les colonnes de la commune sont modifiables, et la modification est PARTAGÉE
                      par toutes les tables : c'est le but, un code postal faux se corrige une
                      fois. L'infobulle le dit sur chaque champ, parce que c'est là qu'on hésite.
                    */}
                    <td
                      className="distances__nom"
                      title="Le nom de la commune, partagé par toutes les tables de distances"
                    >
                      <Champ
                        valeur={l.nom}
                        enAttente={enAttente}
                        ariaLabel={`Nom de ${l.nom}`}
                        onValider={(valeur) => {
                          if (valeur !== "") agir(modifierCommuneDistance(l.communeId, { nom: valeur }));
                        }}
                      />
                    </td>
                    <td className="col-nombre" title="Le code postal, partagé par toutes les tables">
                      <Champ
                        valeur={l.codePostal}
                        enAttente={enAttente}
                        ariaLabel={`Code postal de ${l.nom}`}
                        onValider={(valeur) => {
                          if (valeur !== "") {
                            agir(modifierCommuneDistance(l.communeId, { codePostal: valeur }));
                          }
                        }}
                      />
                    </td>
                    <td className="col-nombre" title="Le département, jamais déduit du code postal">
                      <Champ
                        valeur={l.codeDepartement ?? ""}
                        placeholder="—"
                        enAttente={enAttente}
                        ariaLabel={`Département de ${l.nom}`}
                        onValider={(valeur) => {
                          agir(
                            modifierCommuneDistance(l.communeId, {
                              codeDepartement: valeur === "" ? null : valeur,
                            }),
                          );
                        }}
                      />
                    </td>
                    <td className="col-nombre">
                      <Champ
                        valeur={l.latitude ?? ""}
                        placeholder="—"
                        enAttente={enAttente}
                        ariaLabel={`Latitude de ${l.nom}`}
                        onValider={(valeur) => {
                          agir(
                            modifierCommuneDistance(l.communeId, {
                              latitude: valeur === "" ? null : valeur,
                            }),
                          );
                        }}
                      />
                    </td>
                    <td className="col-nombre">
                      <Champ
                        valeur={l.longitude ?? ""}
                        placeholder="—"
                        enAttente={enAttente}
                        ariaLabel={`Longitude de ${l.nom}`}
                        onValider={(valeur) => {
                          agir(
                            modifierCommuneDistance(l.communeId, {
                              longitude: valeur === "" ? null : valeur,
                            }),
                          );
                        }}
                      />
                    </td>
                    {/* La distance et le prix appartiennent à CETTE table : ils ne bougent que
                        pour ce site. */}
                    <td
                      className="col-nombre distances__manque"
                      title="La distance depuis CE site — propre à cette table"
                    >
                      <Champ
                        valeur={l.distanceKm ?? ""}
                        placeholder="—"
                        enAttente={enAttente}
                        ariaLabel={`Distance de ${l.nom}`}
                        onValider={(valeur) => {
                          agir(fixerDistance(l.id, { distanceKm: valeur === "" ? null : valeur }));
                        }}
                      />
                    </td>
                    <td className="col-nombre" title="Le prix de carburant du classeur — propre à cette table">
                      <Champ
                        valeur={l.prixCarburant ?? ""}
                        placeholder="—"
                        enAttente={enAttente}
                        ariaLabel={`Prix de carburant de ${l.nom}`}
                        onValider={(valeur) => {
                          agir(
                            fixerDistance(l.id, { prixCarburant: valeur === "" ? null : valeur }),
                          );
                        }}
                      />
                    </td>
                    <td>
                      {/*
                        **Retirer une ville la retire de TOUTES les tables.** *Chris, 02/09/2026 :
                        « la liste des communes doit être identique pour tous les sites ».* La
                        retirer d'une seule ferait diverger les listes, et deux sites ne seraient
                        plus comparables. D'où la confirmation, qui dit combien de tables.
                      */}
                      <button
                        type="button"
                        className="lien"
                        disabled={enAttente}
                        title="Supprimer cette commune de TOUTES les tables et du référentiel : la liste doit rester identique pour tous les sites."
                        onClick={() => {
                          const combien = distancier.tables.length;
                          if (
                            !window.confirm(
                              `Supprimer « ${l.nom} » (${l.codePostal}) des ${String(combien)} table(s) ` +
                                "de distances ? La liste des communes doit rester identique pour tous " +
                                "les sites, donc la ville part de partout, avec ses distances.",
                            )
                          ) {
                            return;
                          }
                          agir(retirerCommuneDeLaTable(l.id));
                        }}
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
                {contenu.lignes.length === 0 && (
                  <tr>
                    <td colSpan={8} className="vide">
                      {rechercheAppliquee === ""
                        ? "Aucune commune dans cette table. En ajouter une ci-dessous, ou déposer le classeur."
                        : "Aucune commune ne répond à cette recherche."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <AjoutDeCommune tableId={contenu.table.id} enAttente={enAttente} onAgir={agir} />
        </section>
      )}
    </>
  );
}
